import { NavLink, useNavigate } from "react-router";
import {
  LayoutDashboard,
  Clock,
  Calendar,
  Tag,
  Users,
  LogOut,
  Film,
  Clapperboard,
  Flag,
  MapPin,
  Globe,
  CalendarCheck,
  CalendarDays,
  History,
  Activity,
  UserCog,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useLayout } from "./LayoutContext";
import { useStats } from "@/hooks/useStats";
import { useReports } from "@/hooks/useReports";

type BadgeKey = "pending" | "reports";

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  /** NavLink `end` (exact match) — used for index-like routes. */
  end?: boolean;
  badge?: BadgeKey;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const adminGroups: NavGroup[] = [
  {
    label: "Treść",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Pulpit", end: true },
      { to: "/pending", icon: Clock, label: "Oczekujące", badge: "pending" },
      { to: "/events", icon: Calendar, label: "Wszystkie wydarzenia" },
      { to: "/parent-events", icon: CalendarDays, label: "Cykle wydarzeń" },
      { to: "/movies", icon: Film, label: "Filmy" },
    ],
  },
  {
    label: "Słowniki",
    items: [
      { to: "/categories", icon: Tag, label: "Kategorie" },
      { to: "/venues", icon: MapPin, label: "Lokalizacje" },
      { to: "/users", icon: Users, label: "Użytkownicy" },
    ],
  },
  {
    label: "Automatyzacja",
    items: [
      { to: "/reports", icon: Flag, label: "Zgłoszenia", badge: "reports" },
      { to: "/scrapers/ock", icon: Clapperboard, label: "OCK (skrobak)" },
      { to: "/ai-agent/sources", icon: Globe, label: "Agent AI — Źródła" },
      { to: "/ai-agent/events", icon: CalendarCheck, label: "Agent AI — Wydarzenia" },
      { to: "/ai-agent/runs", icon: History, label: "Agent AI — Historia" },
    ],
  },
  {
    label: "System",
    items: [{ to: "/system-logs", icon: Activity, label: "Logi systemu" }],
  },
];

const organizerGroups: NavGroup[] = [
  {
    label: "Organizator",
    items: [
      { to: "/my-events", icon: Calendar, label: "Moje wydarzenia", end: true },
      { to: "/my-profile", icon: UserCog, label: "Moje dane" },
    ],
  },
];

function NavRow({
  item,
  collapsed,
  badgeValue,
  badgeLabel,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  badgeValue?: number;
  /** Optional display string; falls back to `badgeValue`. Useful for "1+" when the count is approximate. */
  badgeLabel?: string;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  const showBadge = badgeValue != null && badgeValue > 0;

  // Use a STATIC className string (not the function form) + the `active` class
  // that NavLink adds automatically, styled via Tailwind `[&.active]:` selectors.
  // The function-className form combined with a children render-prop was being
  // stringified by the bundler in some builds, leaving the link with no
  // computed `justify-content` (icons hugged the left edge when collapsed).
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={cn(
        "group/navitem relative flex items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition-colors",
        "text-pulso-muted hover:bg-black/5 hover:text-foreground",
        "[&.active]:bg-pulso-primary/10 [&.active]:text-pulso-primary",
        collapsed ? "justify-center px-0" : "px-3",
      )}
    >
      <Icon className="h-[18px] w-[18px] shrink-0 text-current" />
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {!collapsed && showBadge && (
        <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-pulso-orange px-1.5 text-[11px] font-bold text-white">
          {badgeLabel ?? badgeValue}
        </span>
      )}
      {collapsed && showBadge && (
        <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-pulso-orange ring-2 ring-pulso-surface" />
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">
          {item.label}
          {showBadge ? ` (${badgeLabel ?? badgeValue})` : ""}
        </TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

export function Sidebar() {
  const { signOut, userRole } = useAuth();
  const navigate = useNavigate();
  const { collapsed, toggleCollapsed, setMobileOpen } = useLayout();
  const isOrganizer = userRole === "organizer";
  const groups = isOrganizer ? organizerGroups : adminGroups;

  // Badge data — only meaningful for admins.
  const { data: stats } = useStats();
  // We only need to know whether there's at least one open report. Fetching with
  // `limit: 1` is cheap; `hasMore` distinguishes "exactly N" from "N or more"
  // so the badge isn't artificially capped at 1.
  const { data: openReports } = useReports(isOrganizer ? {} : { status: "open", limit: 1 });
  const openReportsCount = openReports?.data.length ?? 0;
  const reportsBadgeValue = isOrganizer ? undefined : openReportsCount;
  const reportsBadgeLabel =
    !isOrganizer && openReportsCount > 0 && openReports?.hasMore
      ? `${openReportsCount}+`
      : undefined;
  const badges: Record<BadgeKey, number | undefined> = {
    pending: isOrganizer ? undefined : stats?.pendingEvents,
    reports: reportsBadgeValue,
  };
  const badgeLabels: Partial<Record<BadgeKey, string>> = {
    reports: reportsBadgeLabel,
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r border-black/5 bg-pulso-surface transition-[width] duration-200",
        collapsed ? "w-[68px]" : "w-60",
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          "flex items-center gap-2.5 border-b border-black/5 px-4 py-5",
          collapsed && "justify-center px-0",
        )}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-pulso-gradient shadow-sm">
          <img src="/pulso-logo.svg" alt="PulsO" className="h-6 w-6" />
        </span>
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-brand text-[22px] font-bold leading-none tracking-tight text-foreground">
              PulsO
            </div>
            <div className="mt-0.5 text-[11px] text-pulso-muted">
              {isOrganizer ? "panel organizatora" : "panel admina"}
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className={cn("flex-1 space-y-1 overflow-y-auto py-3", collapsed ? "px-0" : "px-2.5")}>
        {groups.map((group) => (
          <div key={group.label} className="pt-2 first:pt-0">
            {collapsed ? (
              <div className="mx-auto my-2 h-px w-6 bg-black/5 first:hidden" />
            ) : (
              <div className="px-3 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-pulso-muted/70">
                {group.label}
              </div>
            )}
            {group.items.map((item) => (
              <NavRow
                key={item.to}
                item={item}
                collapsed={collapsed}
                badgeValue={item.badge ? badges[item.badge] : undefined}
                badgeLabel={item.badge ? badgeLabels[item.badge] : undefined}
                onNavigate={closeMobile}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Footer: collapse toggle + logout */}
      <div className={cn("space-y-1 border-t border-black/5 py-3", collapsed ? "px-0" : "px-2.5")}>
        <button
          type="button"
          onClick={toggleCollapsed}
          className={cn(
            "hidden w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-pulso-muted transition-colors hover:bg-black/5 hover:text-foreground lg:flex",
            collapsed && "justify-center px-0",
          )}
          aria-label={collapsed ? "Rozwiń menu" : "Zwiń menu"}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" />
          ) : (
            <>
              <PanelLeftClose className="h-[18px] w-[18px] shrink-0" />
              <span>Zwiń menu</span>
            </>
          )}
        </button>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={cn(
            "w-full justify-start gap-3 text-sm text-pulso-muted hover:bg-black/5 hover:text-foreground",
            collapsed && "justify-center px-0",
          )}
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && "Wyloguj się"}
        </Button>
      </div>
    </aside>
  );
}
